import { NgModule, provideAppInitializer, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { MatSidenavModule } from '@angular/material/sidenav';
import { QuillConfigModule, QuillModule } from 'ngx-quill';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from './layout/layout.module';
import { AddHeadersInterceptor } from './interceptors/httpconfig.interceptor';
import { NgChartsModule } from 'ng2-charts';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import {
  MsalGuard,
  MsalBroadcastService,
  MsalModule,
  MSAL_GUARD_CONFIG,
  MsalService,
  MsalGuardConfiguration,
  MSAL_INSTANCE,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { msalConfig } from './auth-config';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { RoleSearchMultipleModule } from './shared/components/role-search-multiple/role-search-multiple.module';
import { AuthService } from './services/auth.service';
import { PermissionRefreshService } from './services/permission-refresh.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  const publicClientApplication = new PublicClientApplication(msalConfig);
  publicClientApplication.initialize();
  return publicClientApplication;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', 'User.Read'],
    },
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSidenavModule,
    LayoutModule,
    QuillModule.forRoot(),
    QuillConfigModule.forRoot({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }],
          ['link', 'image'],
        ],
      },
      placeholder: 'Enter note here...',
    }),
    NgChartsModule,
    MatButtonModule,
    MatToolbarModule,
    MatListModule,
    MsalModule,
    RoleSearchMultipleModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddHeadersInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideAppInitializer(async () => {
      const auth = inject(AuthService);
      const refresh = inject(PermissionRefreshService);
      await auth.ensureMsalInitialized();
      if (auth.isLoggedIn()) {
        const objectId = auth.getExternalObjectId();
        if (objectId) {
          refresh.start(objectId);
        }
      }
    })
  ],
})
export class AppModule { }
